import type { ExperienceListDto } from "@intelligems/ig-types";

import { shortDateTimeConverter } from "@src/utils/dateConverters";

type TimelineExperience = Pick<
  ExperienceListDto,
  "status" | "archivedAtTs" | "startedAtTs" | "endedAtTs"
> & {
  createdAtTs?: number | null;
};

// Live states (running/active/paused) show a runtime next to the status label;
// pending, ended, and archived rows do not.
export const isLiveExperience = (
  experience: Pick<ExperienceListDto, "status" | "archivedAtTs">
) =>
  !experience.archivedAtTs &&
  (experience.status === "started" || experience.status === "paused");

const getYearInTimezone = (ts: number, timeZone?: string) =>
  Number(
    new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone }).format(ts)
  );

// Timeline column copy: live states show "Started {date}", pending shows
// "Created {date}", ended/archived show "{start} – {end}". The year is only
// shown when a date falls outside the current year; a same-year range shows it
// once at the end (e.g. "Apr 2 – Apr 20, 2025").
export const getExperienceTimelineLabel = (
  experience: TimelineExperience,
  timezone?: string | null,
  now: number = Date.now()
): string | null => {
  const timeZone = timezone ?? undefined;
  const currentYear = getYearInTimezone(now, timeZone);
  const formatDate = (ts: number) =>
    shortDateTimeConverter(
      ts,
      undefined,
      timeZone,
      false,
      getYearInTimezone(ts, timeZone) !== currentYear
    );

  const { archivedAtTs, createdAtTs, endedAtTs, startedAtTs, status } =
    experience;
  const isFinished = Boolean(archivedAtTs) || status === "ended";

  if (startedAtTs && isFinished) {
    const endTs = endedAtTs ?? archivedAtTs;
    if (!endTs) {
      return `Started ${formatDate(startedAtTs)}`;
    }

    const startYear = getYearInTimezone(startedAtTs, timeZone);
    const endYear = getYearInTimezone(endTs, timeZone);
    const start =
      startYear === endYear
        ? shortDateTimeConverter(startedAtTs, undefined, timeZone, false, false)
        : formatDate(startedAtTs);
    return `${start} – ${formatDate(endTs)}`;
  }

  if (startedAtTs) {
    return `Started ${formatDate(startedAtTs)}`;
  }

  if (createdAtTs) {
    return `Created ${formatDate(createdAtTs)}`;
  }

  return null;
};
